import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Canale, FunzioneImpianto, RispostaComando, SessioneTecnica, Zona } from '@emacomfort/domain';
import { ErroreAutorizzazione, type AreaTecnicaClient, type DeviceClient } from '@emacomfort/device-client';
import { useDevice, useLettura, useStato, type LetturaVista } from '@/app/DeviceProvider';
import { useSessioneTecnica } from '@/app/sessioneTecnica';

export function useCanalePerFunzione(funzione: FunzioneImpianto, generatoreId?: string): Canale | undefined {
  return useStato((s) =>
    s.impianto?.canali.find(
      (c) => c.assegnazione.tipo === 'funzione' && c.assegnazione.funzione === funzione && (generatoreId === undefined || c.assegnazione.generatoreId === generatoreId),
    ),
  );
}

export function useValoreFunzione(funzione: FunzioneImpianto, generatoreId?: string): LetturaVista & { canale?: Canale } {
  const canale = useCanalePerFunzione(funzione, generatoreId);
  const lettura = useLettura(canale?.id);
  return { ...lettura, canale };
}

/** Riferimenti stabili per i selettori: un `[]` nuovo a ogni chiamata farebbe ri-renderizzare all'infinito. */
export const NESSUNA_ZONA: Zona[] = [];
export const NESSUN_ID: string[] = [];

export function useZone(): Zona[] {
  return useStato((s) => s.stato?.zone ?? s.impianto?.zone ?? NESSUNA_ZONA);
}

export function useZoneInDomanda(): string[] {
  return useStato((s) => s.stato?.zoneInDomanda ?? NESSUN_ID);
}

/** Temperatura media delle zone abilitate con lettura valida. */
export function useMediaAmbiente(): number | null {
  const zone = useZone();
  const letture = useStato((s) => s.letture);
  return useMemo(() => {
    const valori = zone.filter((z) => z.abilitata).map((z) => letture[z.canaleSonda]?.v).filter((v): v is number => typeof v === 'number');
    if (valori.length === 0) return null;
    return valori.reduce((a, b) => a + b, 0) / valori.length;
  }, [zone, letture]);
}

/** Stato di un attuatore, o `undefined` se il canale non esiste. */
export function useAttuatore(canaleId: string | undefined) {
  return useStato((s) => (canaleId ? s.attuatori[canaleId] : undefined));
}

/**
 * Se questo impianto accetta comandi.
 *
 * Un impianto letto da un connettore di terze parti non li accetta e non li
 * accetterà mai: il rifiuto è strutturale, non una condizione temporanea
 * (ADR-060 §1 — non esiste un secondo punto di applicazione nel luogo
 * dell'effetto fisico). L'interfaccia deve saperlo PRIMA di offrire il
 * comando, non scoprirlo dal rifiuto: un pulsante che può solo fallire è una
 * promessa che l'app non può mantenere.
 *
 * La domanda si fa alle sorgenti, non al tipo di centralina: un impianto può
 * averne più d'una, e basta che una sola sia comandabile perché ci sia
 * qualcosa da comandare. In assenza di sorgenti dichiarate resta il tipo di
 * centralina, che è ciò che gli impianti più vecchi dichiarano.
 */
export function useComandabile(): boolean {
  return useStato((s) => {
    const impianto = s.impianto;
    if (!impianto) return false;
    if (impianto.sorgenti.length === 0) return impianto.centralina.tipo === 'emacomfort';
    return impianto.sorgenti.some((so) => so.comandabile);
  });
}

/**
 * Valore locale con invio differito: il tocco dà riscontro immediato (RNF-11),
 * il comando parte una volta sola quando l'utente smette di toccare.
 */
export function useValoreDifferito(valoreRemoto: number, invia: (v: number) => Promise<RispostaComando> | void, ritardoMs = 700) {
  const [locale, setLocale] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ultimoInviato = useRef<number | null>(null);

  useEffect(() => {
    // Quando la centralina conferma il valore inviato, il locale decade e resta quello reale.
    if (locale !== null && ultimoInviato.current !== null && valoreRemoto === ultimoInviato.current) {
      ultimoInviato.current = null;
      setLocale(null);
    }
  }, [valoreRemoto, locale]);

  const imposta = useCallback(
    (v: number) => {
      setLocale(v);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        ultimoInviato.current = v;
        const esito = invia(v);
        if (esito) {
          void esito.then((r) => {
            // Comando non applicato: il valore mostrato torna a quello realmente in vigore (regolazione-utente.md).
            if (r.esito !== 'applicato' && ultimoInviato.current === v) {
              ultimoInviato.current = null;
              setLocale(null);
            }
          });
        }
      }, ritardoMs);
    },
    [invia, ritardoMs],
  );

  const annulla = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    ultimoInviato.current = null;
    setLocale(null);
  }, []);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { valore: locale ?? valoreRemoto, inAttesa: locale !== null, imposta, annulla };
}

/** Client dell'area tecnica con sessione corrente; un rifiuto di autorizzazione chiude la sessione locale. */
export function useTecnico(): { sessione: SessioneTecnica; tecnico: AreaTecnicaClient; client: DeviceClient; esegui<T>(f: (t: AreaTecnicaClient, s: SessioneTecnica) => Promise<T>): Promise<T | undefined> } {
  const { client } = useDevice();
  const sessione = useSessioneTecnica((s) => s.sessione);
  const esci = useSessioneTecnica((s) => s.esci);
  if (!sessione) throw new Error('useTecnico senza sessione');
  const esegui = useCallback(
    async <T,>(f: (t: AreaTecnicaClient, s: SessioneTecnica) => Promise<T>): Promise<T | undefined> => {
      try {
        return await f(client.tecnico, sessione);
      } catch (e) {
        if (e instanceof ErroreAutorizzazione) {
          esci('scaduta');
          return undefined;
        }
        throw e;
      }
    },
    [client, sessione, esci],
  );
  return { sessione, tecnico: client.tecnico, client, esegui };
}

/** Interroga periodicamente una risorsa dell'area tecnica. */
export function useSondaggio<T>(carica: () => Promise<T | undefined>, intervalloMs: number, dipendenze: unknown[] = []): { dati: T | null; ricarica: () => Promise<void> } {
  const [dati, setDati] = useState<T | null>(null);
  const ricarica = useCallback(async () => {
    const d = await carica();
    if (d !== undefined) setDati(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carica, ...dipendenze]);
  useEffect(() => {
    void ricarica();
    const id = setInterval(() => void ricarica(), intervalloMs);
    return () => clearInterval(id);
  }, [ricarica, intervalloMs]);
  return { dati, ricarica };
}

export function scaricaCsv(nomeFile: string, intestazione: string[], righe: Array<Array<string | number | null>>, preambolo: string[] = []): void {
  const esc = (v: string | number | null) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const testo = [...preambolo.map((p) => `# ${p}`), intestazione.join(';'), ...righe.map((r) => r.map(esc).join(';'))].join('\n');
  const blob = new Blob([`﻿${testo}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
